import { z } from 'zod';
import { PlatformSchema } from './platform.js';
import { ChatUserRefSchema } from './chat-user-ref.js';

export const ModerationActionKindSchema = z.enum(['ban', 'timeout', 'delete', 'broadcast']);
export type ModerationActionKind = z.infer<typeof ModerationActionKindSchema>;

export const BanActionSchema = z.object({
  kind: z.literal('ban'),
  platform: PlatformSchema,
  liveChatId: z.string().min(1),
  target: ChatUserRefSchema,
  reason: z.string().max(500).optional(),
});
export type BanAction = z.infer<typeof BanActionSchema>;

export const TimeoutActionSchema = z.object({
  kind: z.literal('timeout'),
  platform: PlatformSchema,
  liveChatId: z.string().min(1),
  target: ChatUserRefSchema,
  durationSeconds: z.number().int().min(1).max(1209600),
  reason: z.string().max(500).optional(),
});
export type TimeoutAction = z.infer<typeof TimeoutActionSchema>;

export const DeleteActionSchema = z.object({
  kind: z.literal('delete'),
  platform: PlatformSchema,
  liveChatId: z.string().min(1),
  messageExternalId: z.string().min(1),
});
export type DeleteAction = z.infer<typeof DeleteActionSchema>;

export const BroadcastTargetSchema = z.object({
  platform: PlatformSchema,
  liveChatId: z.string().min(1),
});
export type BroadcastTarget = z.infer<typeof BroadcastTargetSchema>;

export const BroadcastActionSchema = z.object({
  // #75: `kind` obrigatório no schema canônico — discriminator do union não pode
  // ser opcional sob pena de enfraquecer a garantia de discriminação. O body
  // HTTP (sem `kind`) usa BroadcastActionBodySchema.
  kind: z.literal('broadcast'),
  text: z.string().trim().min(1).max(500),
  targets: z
    .array(BroadcastTargetSchema)
    .min(1)
    .max(2)
    .refine((targets) => new Set(targets.map((target) => target.platform)).size === targets.length, {
      message: 'targets devem ter plataformas únicas',
    }),
});
export type BroadcastAction = z.infer<typeof BroadcastActionSchema>;

// #75: forma do body HTTP de POST /moderation/broadcast (sem `kind`, que a rota
// reinjeta ao chamar o service). Derivado do canônico pra não duplicar regras.
export const BroadcastActionBodySchema = BroadcastActionSchema.omit({ kind: true });
export type BroadcastActionBody = z.infer<typeof BroadcastActionBodySchema>;

export const ModerationActionSchema = z.discriminatedUnion('kind', [
  BanActionSchema,
  TimeoutActionSchema,
  DeleteActionSchema,
  BroadcastActionSchema,
]);
export type ModerationAction = z.infer<typeof ModerationActionSchema>;

export const ActionResultSchema = z.object({
  ok: z.boolean(),
  errorCode: z.string().optional(),
  errorMessage: z.string().optional(),
  externalMessageId: z.string().optional(),
});
export type ActionResult = z.infer<typeof ActionResultSchema>;

export const BroadcastErrorCodeSchema = z.enum([
  'token_refresh_failed',
  'platform_not_connected',
  'not_moderator',
  'rate_limited_upstream',
  'quota_exhausted',
  'text_too_long',
  'platform_error',
  'internal_error',
]);
export type BroadcastErrorCode = z.infer<typeof BroadcastErrorCodeSchema>;

export const BroadcastTargetResultSchema = z.object({
  platform: PlatformSchema,
  ok: z.boolean(),
  externalMessageId: z.string().nullable(),
  errorCode: BroadcastErrorCodeSchema.nullable(),
});
export type BroadcastTargetResult = z.infer<typeof BroadcastTargetResultSchema>;

export const BroadcastResponseSchema = z.object({
  broadcastId: z.string().uuid(),
  results: z.array(BroadcastTargetResultSchema),
});
export type BroadcastResponse = z.infer<typeof BroadcastResponseSchema>;
